"use client";

import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { FaPause, FaPlay } from "react-icons/fa6";

type LoopVideoProps = {
  src: string;
  /** Accessible description of what the demo shows. */
  label?: string;
  /** Classes of the <video> itself (size, fit, radius). */
  className?: string;
  /** Classes of the wrapper that positions the pause button over the video. */
  wrapperClassName?: string;
};

/**
 * A silent looping product demo (the lighter replacement of a GIF), with the two things a
 * never-ending animation owes its viewers: a pause button (WCAG 2.2.2, moving content
 * longer than 5 s) and no autoplay when the visitor asked the system for reduced motion
 * (the first frame shows, the button starts it).
 */
export function LoopVideo({ src, label, className = "", wrapperClassName = "relative" }: LoopVideoProps) {
  const intl = useIntl();
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    void v.play().catch(() => {
      // Autoplay refused (data saver, low-power mode): the play button stays available.
    });
  }, []);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) void v.play().catch(() => {});
    else v.pause();
  };

  return (
    <span className={`group/loop block ${wrapperClassName}`}>
      <video
        ref={ref}
        src={src}
        className={className}
        muted
        loop
        playsInline
        preload="metadata"
        aria-label={label || undefined}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      <button
        type="button"
        onClick={toggle}
        aria-label={intl.formatMessage({ id: playing ? "a11y.video.pauseLoop" : "a11y.video.playLoop" })}
        className="absolute bottom-2 right-2 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-title-blue/75 text-white opacity-80 shadow-sm backdrop-blur-sm transition-opacity duration-200 hover:opacity-100 focus-visible:opacity-100 group-hover/loop:opacity-100"
      >
        {playing ? <FaPause aria-hidden="true" className="text-xs" /> : <FaPlay aria-hidden="true" className="ml-0.5 text-xs" />}
      </button>
    </span>
  );
}
